import { Component, HostListener, ViewContainerRef, effect, inject, viewChild, viewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Observable } from 'rxjs';

import { NgxPaginationModule } from 'ngx-pagination';

import { RegisterComponent } from '../../../components/component-lookup';
import { CanDeactivateComponent } from '../../../../shared/guards/pending-changes.guard';
import { ConfirmDialogOptions, DialogService } from '../../../../shared/services/dialog.service';
import { Group, UpdateGroup, UpdateGroupInstitution } from '../../../services/group.service';
import { InstitutionProfile, InstitutionProfileService } from '../../../services/ncua/institution-profile.service';
import { StateService } from '../../../services/state.service';
import { ProductNavComponent } from '../../../components/product-nav/product-nav.component';
import { InstitutionAutocompleteComponent } from '../../../components/institution-autocomplete/institution-autocomplete.component';
import { RenameGroupDialogComponent } from '../../rename-group-dialog/rename-group-dialog.component';
import { Institution } from '../../../services/institution.service';

@RegisterComponent('NcuaManageGroups')
@Component({
  selector: 'app-ncua-manage-groups',
  standalone: true,
  imports: [CommonModule, RouterModule, NgxPaginationModule, ProductNavComponent, InstitutionAutocompleteComponent],
  templateUrl: './manage-groups.component.html',
  styleUrl: './manage-groups.component.scss'
})
export class NcuaManageGroupsComponent implements CanDeactivateComponent {
  institutionAutocompletes = viewChildren(InstitutionAutocompleteComponent);
  dialog = viewChild.required('dialog', { read: ViewContainerRef });

  private dialogService = inject(DialogService);
  private profileService = inject(InstitutionProfileService);
  private stateService = inject(StateService);
  readonly groups = inject(StateService).groups;
  readonly institution = inject(StateService).institution;

  clientProfile?: InstitutionProfile;
  isDirty = false;
  numAdded = 0;
  numRemoved = 0;
  loading = false;

  @HostListener('window:beforeunload', ['$event'])
  onbeforeunload(event: { preventDefault: () => void; returnValue: boolean; }) {
    if (this.isDirty) {
      event.preventDefault();
      event.returnValue = false;
    }
  }

  constructor() {
    effect(() => {
      if (this.institution()) {
        this.profileService.getInstitutionProfile(this.institution()!.iid).subscribe(profile => {
          this.clientProfile = profile;
        });
      }

      if (this.groups()) {
        this.initGroups([])
      }
    });
  }

  initGroups(updatedGroups: UpdateGroup[]) {
    this.numAdded = 0;
    this.numRemoved = 0;
    this.isDirty = false;
    this.groups().forEach(group => {
      group.sortColumn = 'name';
      group.sortDir = 'asc';
      group.page = 1;

      if (updatedGroups.find(g => g.groupId == group.groupId)) {
        group.isOpen = true;
      }
      else {
        group.isOpen = false;
      }

      group.institutionProfiles.sort(this.dynamicSort(group.sortColumn as keyof InstitutionProfile, group.sortDir));
      group.institutionProfiles.forEach(institution => {
        institution.isAdded = false;
        institution.isRemoved = false;
      });
    });
  }

  renameGroup(groupId: string) {
    const group = this.groups().find(g => g.groupId == groupId);
    if (group != undefined) {
      const options = {
        groupId: groupId,
        name: group.name,
        description: group.description
      };

      this.dialogService.show(this.dialog(), RenameGroupDialogComponent, options)
        .subscribe(modalResult => {
          if (modalResult.isSubmitted) {
            const updateGroup: UpdateGroup = modalResult.value as UpdateGroup;
            this.stateService.updateGroups(this.institution()!.iid, [updateGroup]);
          }
        });
    }
  }

  deleteGroup(groupId: string) {
    const group = this.groups().find(g => g.groupId == groupId);
    const options: ConfirmDialogOptions = {
      title: 'Delete Group?',
      body: group?.name
    }

    this.dialogService.confirm(this.dialog(), options)
      .subscribe(modelResult => {
        if (modelResult.isSubmitted) {
          this.stateService.deleteGroup(this.institution()!.iid, groupId);
        }
      });
  }

  discardChanges() {
    this.groups().forEach(group => {
      group.institutionProfiles.forEach(institition => {
        if (institition.isAdded) {
          group.institutionProfiles = group.institutionProfiles.filter(i => i != institition);
        }
        institition.isRemoved = false;
      });
    });
    this.numAdded = 0;
    this.numRemoved = 0;
    this.isDirty = false;
  }

  saveChanges() {
    const updateGroups: UpdateGroup[] = [];

    this.groups().forEach(group => {
      const updateInstitutions: UpdateGroupInstitution[] = [];
      group.institutionProfiles.forEach(institition => {
        if (institition.isAdded || institition.isRemoved) {
          if (!(institition.isAdded && institition.isRemoved)) {
            updateInstitutions.push({ iid: institition.iid, isAdded: institition.isAdded, isRemoved: institition.isRemoved });
          }
        }
      });
      if (updateInstitutions.length > 0) {
        updateGroups.push({ groupId: group.groupId, name: group.name, description: group.description, institutions: updateInstitutions });
      }
    });

    if (updateGroups.length > 0) {
      this.updateGroups(updateGroups);
    }
  }

  updateGroups(updateGroups: UpdateGroup[]) {
    this.stateService.updateGroups(this.institution()!.iid, updateGroups).subscribe(groupIds => {
      setTimeout(() => {
        this.groups().forEach(group => {
          if (groupIds.includes(group.groupId)) {
            this.toggleView(group);
          }
        });
      });
    });
  }

  onSelectInstitution(institution: Institution, groupId: string) {
    const group = this.groups().find(g => g.groupId == groupId);
    if (group != undefined) {
      setTimeout(() => {
        this.institutionAutocompletes().forEach(autocomplete => autocomplete.clearInstitution());
      });
      this.profileService.getInstitutionProfile(institution.iid).subscribe(profile => {
        profile.isAdded = true;
        profile.isRemoved = false;
        group.institutionProfiles.unshift(profile);
        this.numAdded++;
        this.isDirty = true;
      });
    }
  }

  removeInstitution(groupId: string, iid: number) {
    const group = this.groups().find(g => g.groupId == groupId)!;
    const institution = group.institutionProfiles.find(i => i.iid == iid)!;
    institution.isRemoved = true;

    if (institution.isAdded) {
      setTimeout(() => {
        const index = group.institutionProfiles.findIndex(i => i.iid == iid);
        group.institutionProfiles.splice(index, 1);
        this.numAdded--;
        if (this.numAdded == 0 && this.numRemoved == 0) {
          this.isDirty = false;
        }
      }, 3000)
    }
    else {
      this.numRemoved++;
      this.isDirty = true;
    }
  }

  toggleView(group: Group) {
    group.isOpen = !group.isOpen
    if (group.isOpen && group.institutionProfiles.length == 0) {
      this.loading = true;
      this.profileService.getGroupInstitutionProfiles(this.institution()!.iid, group.groupId).subscribe(institutions => {
        institutions.sort((a, b) => (a.name > b.name) ? 1 : -1);
        group.institutionProfiles = institutions;
        this.loading = false;
      });
    }
  }

  onSort(group: Group, column: string): void {
    if (group.sortColumn == column) {
      group.sortDir = group.sortDir == 'asc' ? 'desc' : 'asc';
    }
    else {
      group.sortColumn = column as keyof InstitutionProfile;
      group.sortDir = 'asc';
    }
    group.institutionProfiles.sort(this.dynamicSort<InstitutionProfile>(group.sortColumn as keyof InstitutionProfile, group.sortDir));
  }

  dynamicSort<T>(property: keyof T, dir: string): (a: T, b: T) => number {
    return function (a: T, b: T) {
      const result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0;
      return dir == 'asc' ? result : result * -1;
    }
  }
  
  canDeactivate(): Observable<boolean> | boolean {
    return !this.isDirty;
  }

  usdDisplay(value: number | undefined): string {
    return value != undefined ? new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value / 1000000) + 'MM' : '';
  }

  pctDisplay(value: number | undefined): string {
    return value != undefined ? value.toFixed(2) + '%' : '';
  }

  fixUrl(url: string): string {
    return !/^https?:\/\//i.test(url) ? 'http://' + url : url;
  }
}
